/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { clearTokens } from 'utils/TokenUtils';

import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import { ReactComponent as Logo } from 'asserts/logo/exbody_white.svg';
import { ReactComponent as IconLogOutButton } from 'asserts/icons/ic_log_out_lined.svg';
import { getClient, restfetcher } from 'queryClient';

import Select from 'components/Select/Select';

import { useAtom, useAtomValue } from 'jotai';
import commonMonthAtom from 'atom/commonMonth';
import commonYearAtom from 'atom/commonYear';
import companyInforAtom from 'atom/companyInforAtom';
import userInforAtom from 'atom/userInforAtom';

interface UserInforDataProps {
  yearsList: number[];
}

const Header = ({ yearsList }: UserInforDataProps) => {
  const navigate = useNavigate();
  const queryClient = getClient();

  const [selectedMonth, setSelectedMonth] = useAtom(commonMonthAtom);
  const [selectedYear, setSelectedYear] = useAtom(commonYearAtom);

  const userInfor = useAtomValue(userInforAtom);
  const companyInfor = useAtomValue(companyInforAtom);

  const requestLogout = useMutation({
    mutationFn: () =>
      restfetcher({
        method: 'POST',
        path: 'auth/logout',
        params: {}
      }),

    onSuccess: (data: any) => {
      if (data.result === 'success') {
        localStorage.clear();
        sessionStorage.clear();
        clearTokens();
        navigate('/');
        queryClient.removeQueries();
      }
    }
  });

  const handleClickLogout = async () => {
    requestLogout.mutate();
  };

  const months = Array.from({ length: 13 }, (_, i) => i);

  const isYearsArray = Array.isArray(yearsList);

  const handleSelectChange = (type: 'year' | 'month', value: number) => {
    if (type === 'year') {
      setSelectedYear(value);
    } else {
      setSelectedMonth(value);
    }
  };

  const showName =
    companyInfor.companyname || userInfor.usrname || userInfor.usrname;

  return (
    <Wrapper>
      <LogoWrapper>
        <h1>
          <Logo />
        </h1>
        <NickName>
          <p>{showName}님,</p> &nbsp;
          <p>안녕하세요.</p>
        </NickName>
      </LogoWrapper>
      <RightButtonWrapper>
        <SelectWrapper>
          <Select
            isMonth={true}
            label={selectedMonth === 0 ? '전체' : `${selectedMonth}월`}
            options={months}
            selectedValue={selectedMonth}
            onChange={(value) => handleSelectChange('month', value)}
          />
          <Select
            label={`${selectedYear}년`}
            options={isYearsArray ? yearsList : [selectedYear]}
            selectedValue={selectedYear}
            onChange={(value) => handleSelectChange('year', value)}
          />
        </SelectWrapper>

        <LogOutButton onClick={handleClickLogout}>
          <p>로그아웃</p>
          <IconLogOutButton />
        </LogOutButton>
      </RightButtonWrapper>
    </Wrapper>
  );
};

export default Header;

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  justify-content: space-between;
  height: 64px;
  margin: 0 auto;
  background-color: ${color.bgDashBoard};
  padding: 16px 0;
  z-index: 999;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
    display: block;
    width: 100vw;
    height: 1px;
    border-bottom: 1px solid ${color.white01};
  }
`;

const NickName = styled.div`
  font-size: 14px;
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  & div {
    display: flex;
    align-items: center;
    padding-left: 16px;
  }

  & h1 {
    margin: auto 0;
  }
`;

const RightButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 32px;
`;

const LogOutButton = styled.button`
  display: flex;
  align-items: center;

  color: ${color.white06};

  & p {
    padding-right: 6px;
    font-size: 13px;
    line-height: 16px;
  }
`;

const StatisticsButton = styled.button`
  display: flex;
  padding: 6px 24px;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  border: 1px solid ${color.mobile};
  color: ${color.mobile};
  ${font.caption1_regular};
`;

const SelectWrapper = styled.div`
  display: flex;
  gap: 16px;
`;
